import React, { useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLayerGroup,
  faBuilding,
  faDatabase,
  faChartColumn,
  faEnvelope,
  faLock,
  faEye,
  faEyeSlash,
  faCircleNotch,
  faShieldHalved,
} from "@fortawesome/free-solid-svg-icons";
import "./login.css";

import { useLocation, useNavigate } from "react-router-dom";
import Validation from "../../../helper/Validations";
import { onLogin } from "../../../store/actions/auth";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";

const Login = () => {
  const isLoggedIn = useAppSelector((state) => state?.auth?.loggedIn);
  const loader = useAppSelector((state) => state?.auth?.loading);
  const dispatch = useAppDispatch();
  const [loginData, setLoginData] = useState({
    email: "",
    password: "",
  });

  const [errors, setErrors] = useState({
    email: false,
    password: false,
  });

  const [showPassword, setShowPassword] = useState(false);

  const { state }: any = useLocation();
  const navigate = useNavigate();
  const redirect = state?.redirectTo;
  useEffect(() => {
    if (isLoggedIn) {
      if (!redirect) {
        navigate("/superadmin/dashboard", { replace: true });
      } else {
        // state clear
        window.history.replaceState({}, "");
        // navigate to redirect
        navigate(redirect, { replace: true });
      }
    }
  }, [isLoggedIn, navigate, redirect]);

  const loginSubmit = () => {
    const emailValidation = Validation.isEmail(email);
    const isValid = password === "" || !password ? false : true;
    if (!emailValidation.isValid || !isValid) {
      setErrors({
        email: !emailValidation.isValid,
        password: !isValid,
      });
    } else {
      setErrors({
        email: false,
        password: false,
      });
      dispatch(onLogin(loginData));
    }
  };

  const { email, password } = loginData;

  const onChangeValue = (e: any) => {
    setLoginData({ ...loginData, [e.target.name]: e.target.value });
  };

  const features = [
    {
      icon: faBuilding,
      title: "Company Management",
      desc: "Onboard organizations and manage their teams from one place.",
    },
    {
      icon: faDatabase,
      title: "Question Bank",
      desc: "Build, organize and reuse exam questions across categories.",
    },
    {
      icon: faChartColumn,
      title: "Analytics & Reports",
      desc: "Track performance with clear, actionable insights.",
    },
  ];

  const stats = [
    { value: "12k+", label: "Exams delivered" },
    { value: "500+", label: "Companies" },
    { value: "99.9%", label: "Uptime" },
  ];

  return (
    !isLoggedIn && (
      <div className="ds-login ds-scope">
        {/* ---------- Left: Brand / Trust panel ---------- */}
        <aside className="ds-login-brand">
          <div className="ds-login-brand-inner">
            <div className="ds-login-logo">
              <span className="ds-login-logo-mark">
                <FontAwesomeIcon icon={faLayerGroup} />
              </span>
              <span className="ds-login-logo-text">EXMBNK</span>
            </div>

            <div className="ds-login-brand-copy">
              <span className="ds-login-eyebrow">Examination Platform</span>
              <h1 className="ds-login-headline">
                Manage Your Examination Platform
              </h1>
              <p className="ds-login-tagline">
                A unified workspace to run companies, curate question banks and
                measure outcomes — built for teams that take assessments
                seriously.
              </p>
            </div>

            {/* <ul className="ds-login-features">
              {features.map((f) => (
                <li className="ds-login-feature" key={f.title}>
                  <span className="ds-login-feature-icon">
                    <FontAwesomeIcon icon={f.icon} />
                  </span>
                  <span className="ds-login-feature-body">
                    <span className="ds-login-feature-title">{f.title}</span>
                    <span className="ds-login-feature-desc">{f.desc}</span>
                  </span>
                </li>
              ))}
            </ul> */}

            {/* <div className="ds-login-stats">
              {stats.map((s) => (
                <div className="ds-login-stat" key={s.label}>
                  <span className="ds-login-stat-value">{s.value}</span>
                  <span className="ds-login-stat-label">{s.label}</span>
                </div>
              ))}
            </div> */}

            <div className="ds-login-trust">
              <FontAwesomeIcon icon={faShieldHalved} />
              <span>Enterprise-grade security &amp; access control</span>
            </div>
          </div>
        </aside>

        {/* ---------- Right: Auth card ---------- */}
        <main className="ds-login-main">
          <div className="ds-login-card">
            {/* Compact brand shown on mobile where the panel is hidden */}
            <div className="ds-login-card-brand">
              <span className="ds-login-logo-mark">
                <FontAwesomeIcon icon={faLayerGroup} />
              </span>
              <span className="ds-login-logo-text">EXMBNK</span>
            </div>

            <div className="ds-login-card-head">
              <h2 className="ds-login-title">Welcome back</h2>
              <p className="ds-login-sub">Sign in to your admin dashboard</p>
            </div>

            <Form
              noValidate
              onSubmit={(e) => {
                e.preventDefault();
                loginSubmit();
              }}
            >
              <div className="ds-field">
                <label className="ds-field-label" htmlFor="login-email">
                  Email address
                </label>
                <div
                  className={`ds-input-wrap${
                    errors?.email ? " ds-input-wrap--error" : ""
                  }`}
                >
                  <FontAwesomeIcon
                    icon={faEnvelope}
                    className="ds-input-icon"
                  />
                  <Form.Control
                    id="login-email"
                    type="email"
                    placeholder="name@example.com"
                    name="email"
                    value={email}
                    isInvalid={errors?.email}
                    onChange={(e) => onChangeValue(e)}
                  />
                </div>
                {errors.email && (
                  <span className="ds-field-error">
                    Please enter a valid email
                  </span>
                )}
              </div>

              <div className="ds-field">
                <label className="ds-field-label" htmlFor="login-password">
                  Password
                </label>
                <div
                  className={`ds-input-wrap${
                    errors.password ? " ds-input-wrap--error" : ""
                  }`}
                >
                  <FontAwesomeIcon icon={faLock} className="ds-input-icon" />
                  <Form.Control
                    id="login-password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    name="password"
                    value={password}
                    isInvalid={errors.password}
                    onChange={(e) => onChangeValue(e)}
                  />
                  <button
                    type="button"
                    className="ds-input-toggle"
                    onClick={() => setShowPassword((s) => !s)}
                    aria-label={
                      showPassword ? "Hide password" : "Show password"
                    }
                    tabIndex={-1}
                  >
                    <FontAwesomeIcon
                      icon={showPassword ? faEyeSlash : faEye}
                    />
                  </button>
                </div>
                {errors.password && (
                  <span className="ds-field-error">Password is required</span>
                )}
              </div>

              <button
                type="submit"
                className="ds-btn ds-btn-primary ds-btn-block ds-btn-lg ds-login-submit"
                onClick={loginSubmit}
                disabled={loader}
              >
                {loader ? (
                  <>
                    <FontAwesomeIcon icon={faCircleNotch} spin />
                    Signing in…
                  </>
                ) : (
                  "Sign in"
                )}
              </button>
            </Form>
          </div>

          <p className="ds-login-foot">
            © {new Date().getFullYear()} EXMBNK. All rights reserved.
          </p>
        </main>
      </div>
    )
  );
};

export default Login;
